import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

export const getClients = createAsyncThunk(
  "admin/clientes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/clients");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch clients"
      );
    }
  }
);

export const deleteClient = createAsyncThunk(
  "admin/deleteClient",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/api/admin/client/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch clients"
      );
    }
  }
);

export const editClient = createAsyncThunk(
  "admin/editClient",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/admin/client/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to edit client"
      );
    }
  }
);

export const getPodcastsClient = createAsyncThunk(
  "client/getPodcasts",
  async (_, { rejectWithValue }) => {
    const response = await axios.get("/api/client/podcasts");
    return response.data;
  }
);

const loadClientFromLocalStorage = () => {
  const client = localStorage.getItem("client");
  return client ? JSON.parse(client) : null;
};

const loadPodcastFromLocalStorage = () => {
  const podcast = localStorage.getItem("podcast");
  try {
    return podcast ? JSON.parse(podcast) : null;
  } catch (error) {
    console.error("Failed to parse podcast from localStorage:", error);
    return null;
  }
};

const loadPlanFromLocalStorage = () => {
  const plan = localStorage.getItem("plan");
  try {
    return plan ? JSON.parse(plan) : null;
  } catch (error) {
    console.error("Failed to parse plan from localStorage:", error);
    return null;
  }
};

// /api/prospects/confirm/:id
// body: password, token
const initialState = {
  loading: false,
  error: null,
  listClients: [],
  listClientsCopy: [],
  client: loadClientFromLocalStorage(),
  clientProspect: null,
  podcasts: [],
  podcast: loadPodcastFromLocalStorage(),
  plan: loadPlanFromLocalStorage()
};

const clientesSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setClient: (state, action) => {
      state.client = action.payload;
      localStorage.setItem("client", JSON.stringify(action.payload));
    },
    setPodcast: (state, action) => {
      state.podcast = action.payload;
      localStorage.setItem("podcast", JSON.stringify(action.payload));
    },
    setPlan: (state, action) => {
      state.plan = action.payload;
      localStorage.setItem("plan", JSON.stringify(action.payload))
    },
    sortClientsName: (state, action) => {
      const order = action.payload;
      if (state.listClients) {
        if (order === "asc" || order === "desc") {
          state.listClients.sort((a, b) => {
            const nameA = a.name.toLowerCase();
            const nameB = b.name.toLowerCase();
            if (order === "asc") {
              return nameA.localeCompare(nameB);
            } else if (order === "desc") {
              return nameB.localeCompare(nameA);
            }
            return 0;
          });
        } else if (order === "") {
          state.listClients = [...state.listClientsCopy];
        }
      }
    },
    filterByName: (state, action) => {
      const name = action.payload;
      if (name) {
        state.listClients = state.listClientsCopy.filter((client) =>
          client.name.toLowerCase().includes(name.toLowerCase())
        );
      } else {
        state.listClients = [...state.listClientsCopy];
      }
    },
    orderByPodcasts: (state, acction) => {
      const order = acction.payload;
      if (state.listClients) {
        if (order === "asc" || order === "desc") {
          state.listClients.sort((a, b) => {
            if (order === "asc") {
              return a.totalPodcasts - b.totalPodcasts;
            } else if (order === "desc") {
              return b.totalPodcasts - a.totalPodcasts;
            }
            return 0;
          });
        } else if (order === "") {
          state.listClients = [...state.listClientsCopy];
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.loading = false;
        state.listClients = action.payload;
        state.listClientsCopy = action.payload;
      })
      .addCase(getClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.listClients = state.listClients.filter(
          (client) => client._id !== action.payload._id
        );
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editClient.fulfilled, (state, action) => {
        state.loading = false;
        state.listClients = state.listClients.map((client) =>
          client._id === action.payload._id ? action.payload : client
        );
      })
      .addCase(editClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getPodcastsClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPodcastsClient.fulfilled, (state, action) => {
        state.loading = false;
        state.podcasts = action.payload;
      })
      .addCase(getPodcastsClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setClient,
  sortClientsName,
  filterByName,
  orderByPodcasts,
  setPodcast,
} = clientesSlice.actions;
export default clientesSlice.reducer;
